'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Zap, Loader2, Pause, Play, ExternalLink, DollarSign, Clock, CheckCircle2, AlertCircle } from 'lucide-react'
import { useNexusBackground } from '@/providers/nexus-background-provider'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { isCoreTask, taskHref, taskListHref, taskTarget, taskListTarget } from './task-href'

/**
 * NexusHeaderIndicator
 *
 * Small icon in header that shows Nexus background task status.
 * - Idle: Shows Zap icon (clickable for info)
 * - Active: Shows spinning loader with count badge
 * - Dropdown with task details and cost
 */
export function NexusHeaderIndicator() {
  const {
    activeTasks,
    hasActiveTasks,
    totalCost,
    maxProjectCost,
    lifetimeCost,
    runningCount,
    pendingCount,
    pausedCount,
    pauseTask,
    resumeTask,
    isLoading
  } = useNexusBackground()

  const [isOpen, setIsOpen] = useState(false)

  // Format cost
  const formatCost = (cost) => {
    if (cost < 0.01) return `$${cost.toFixed(4)}`
    return `$${cost.toFixed(2)}`
  }

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now - date
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'gerade eben'
    if (diffMin < 60) return `vor ${diffMin}m`
    const diffHours = Math.floor(diffMin / 60)
    return `vor ${diffHours}h`
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          mode="icon"
          shape="circle"
          className={cn(
            "size-9 relative",
            hasActiveTasks
              ? "hover:bg-purple-500/10 hover:[&_svg]:text-purple-500"
              : "hover:bg-primary/10 hover:[&_svg]:text-primary"
          )}
          title={hasActiveTasks ? `${activeTasks.length} Nexus Tasks aktiv` : "Nexus Background Tasks"}
        >
          {hasActiveTasks ? (
            <>
              <Loader2 className="size-4.5! animate-spin text-purple-500" />
              {/* Badge with count */}
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-purple-500 text-[10px] font-bold text-white">
                {activeTasks.length}
              </span>
            </>
          ) : (
            <Zap className="size-4.5!" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-80 p-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-purple-500/10 to-indigo-500/10">
          <div className="flex items-center gap-2">
            <Zap className="size-4 text-purple-500" />
            <span className="font-semibold text-sm">Nexus Background Tasks</span>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            {/* Aktuell - current session cost */}
            {hasActiveTasks && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <DollarSign className="size-3" />
                <span className="font-mono">{formatCost(totalCost)}</span>
                <span className="text-muted-foreground/60">aktuell</span>
              </div>
            )}
            {/* Projekt - project total + current session */}
            {maxProjectCost > 0 && (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
                <span className="font-mono">{formatCost(maxProjectCost + totalCost)}</span>
                <span>projekt</span>
              </div>
            )}
            {/* Gesamt - lifetime total + current session */}
            {lifetimeCost > 0 && (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
                <span className="font-mono">{formatCost(lifetimeCost + totalCost)}</span>
                <span>gesamt</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : !hasActiveTasks ? (
            <div className="py-6 text-center">
              <div className="flex justify-center mb-3">
                <div className="p-3 rounded-full bg-muted">
                  <CheckCircle2 className="size-6 text-muted-foreground" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Keine aktiven Tasks</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Background Tasks laufen weiter, auch wenn der Browser geschlossen wird
              </p>
              {lifetimeCost > 0 && (
                <div className="mt-4 pt-4 border-t mx-4">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <DollarSign className="size-4 text-muted-foreground" />
                    <span className="font-mono font-medium">{formatCost(lifetimeCost)}</span>
                    <span className="text-muted-foreground">Gesamtkosten</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {activeTasks.map((task) => (
                <motion.div
                  key={task.task_id}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="px-4 py-3 hover:bg-muted/50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {/* Project name with status icon */}
                      <div className="flex items-center gap-2">
                        {task.status === 'running' ? (
                          <Loader2 className="size-3 animate-spin text-green-500 shrink-0" />
                        ) : task.status === 'paused' ? (
                          <Pause className="size-3 text-orange-500 shrink-0" />
                        ) : (
                          <Clock className="size-3 text-yellow-500 shrink-0" />
                        )}
                        <a
                          href={taskHref(task)}
                          target={taskTarget(task)}
                          rel="noopener"
                          onClick={() => setIsOpen(false)}
                          className="text-sm font-medium truncate hover:underline hover:text-primary"
                        >
                          {task.project_name || `Projekt ${task.project_id}`}
                        </a>
                        {isCoreTask(task) && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-600 rounded-full shrink-0">
                            Core
                          </span>
                        )}
                        {task.status === 'paused' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-600 rounded-full">
                            pausiert
                          </span>
                        )}
                      </div>

                      {/* Current step */}
                      {task.current_step && task.status !== 'paused' && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {task.current_step}
                        </p>
                      )}

                      {/* Progress & Cost */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {task.progress_percent > 0 && (
                          <span>{task.progress_percent}%</span>
                        )}
                        {task.estimated_cost_usd > 0 && (
                          <span className="font-mono">{formatCost(task.estimated_cost_usd)}</span>
                        )}
                        <span>{formatTimeAgo(task.created_at)}</span>
                      </div>
                    </div>

                    {/* Pause/Resume button (instead of Cancel - Cancel is in Nexus Chat) */}
                    {task.status === 'paused' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-6 shrink-0 text-muted-foreground hover:text-green-500 hover:bg-green-500/10"
                        onClick={(e) => {
                          e.stopPropagation()
                          resumeTask(task.task_id)
                        }}
                        title="Task fortsetzen"
                      >
                        <Play className="size-3" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-6 shrink-0 text-muted-foreground hover:text-orange-500 hover:bg-orange-500/10"
                        onClick={(e) => {
                          e.stopPropagation()
                          pauseTask(task.task_id)
                        }}
                        title="Task pausieren"
                      >
                        <Pause className="size-3" />
                      </Button>
                    )}
                  </div>

                  {/* Progress bar */}
                  {task.progress_percent > 0 && task.progress_percent < 100 && task.status !== 'paused' && (
                    <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${task.progress_percent}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-muted/30">
          <a
            href={taskListHref(activeTasks)}
            target={taskListTarget(activeTasks)}
            rel="noopener"
            className="flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            onClick={() => setIsOpen(false)}
          >
            <span>{taskListHref(activeTasks) === '/nexus/core' ? 'Nexus Core öffnen' : 'Alle Projekte anzeigen'}</span>
            <ExternalLink className="size-3" />
          </a>
        </div>
      </PopoverContent>
    </Popover>
  )
}
